import type {
  FundTransactionDocumentLinkRequest,
  FundTransactionStatus,
} from '@/types/fund-transactions';

export const FUND_COST_CATEGORIES = [
  'AUDIT',
  'LEGAL',
  'ADMINISTRATION',
  'BANKING',
  'INSURANCE',
  'OTHER',
] as const;

export type FundCostCategory = (typeof FUND_COST_CATEGORIES)[number];

export const FUND_COST_CATEGORY_LABELS: Record<FundCostCategory, string> = {
  AUDIT: 'Audit',
  LEGAL: 'Legal',
  ADMINISTRATION: 'Administration',
  BANKING: 'Banking',
  INSURANCE: 'Insurance',
  OTHER: 'Other',
};

export interface FundCost {
  id: string;
  fund_id: string;
  cost_category: FundCostCategory;
  description: string | null;
  amount: number;
  counterparty_name: string | null;
  counterparty_iban: string | null;
  effective_from: string;
  effective_to: string;
  notes: string | null;
  /** The payment behind the cost. */
  fund_transaction_id: string;
  transaction_status: FundTransactionStatus;
  created_at: string;
  updated_at: string;
}

export interface FundCostCreateResponse {
  id: string;
}

export interface FundCostCreate {
  cost_category: FundCostCategory;
  description?: string | null;
  amount: number;
  counterparty_name?: string | null;
  counterparty_iban?: string | null;
  effective_from: string;
  effective_to: string;
  notes?: string | null;
  /** Linked to the payment the cost creates. */
  documents?: FundTransactionDocumentLinkRequest[];
}

export type FundCostUpdate = Partial<Omit<FundCostCreate, 'documents'>>;

/** Amount and IBAN come from the bank line. */
export interface FundCostDesignate {
  cost_category: FundCostCategory;
  description?: string | null;
  /** The bank's spelling of the payee when omitted. */
  counterparty_name?: string | null;
  effective_from: string;
  effective_to: string;
  notes?: string | null;
  /** Linked to the payment the cost creates. */
  documents?: FundTransactionDocumentLinkRequest[];
}

export interface FundCostDesignateResponse {
  fund_cost_id: string;
  fund_transaction_id: string;
}

export interface FundCostRecognitionEntry {
  recognition_month: string;
  period_end: string;
  amount: number;
  fund_transaction_id: string | null;
  status: FundTransactionStatus | null;
}

export interface FundCostPage {
  items: FundCost[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface FundCostsSummary {
  total_costs: number;
  cost_count: number;
  costs_by_category: Record<string, number>;
}
